import Autocomplete, {createFilterOptions} from '@mui/material/Autocomplete';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import {arrayOf, object, string} from 'prop-types';
import React, {useCallback, useState} from 'react';
import {useController, useFormContext, useFormState} from 'react-hook-form';
const filter = createFilterOptions({ignoreCase: true, trim: true});

function ComboBox({
  name,
  label,
  inputLabel = 'Add value',
  defaultValue = '',
  possibleValues,
  rules,
}) {
  const [value, setValue] = useState(defaultValue);
  const {control} = useFormContext();
  const {field} = useController({
    name,
    rules,
    control,
    defaultValue,
  });
  const {errors} = useFormState({control, name});

  const handleChange = useCallback(
    (event, option) => {
      let value = '';
      if (option) {
        if (typeof option === 'string') {
          value = option;
        } else {
          value = option.value;
        }
      }
      setValue(value);
      field.onChange(value);
    },
    [field],
  );

  const handleInputChange = useCallback((event, value) => {
    setValue(value);
  }, []);

  const filterOptions = useCallback(
    (options, params) => {
      const filtered = filter(options, params);
      const value = params.inputValue.toLowerCase().trim();
      const isExisting = possibleValues.some(candidate => value === candidate);
      if (value && !isExisting) {
        filtered.push({value, label: `Add "${value}"`});
      }

      return filtered;
    },
    [possibleValues],
  );

  const errorMessage = errors[name]?.message;

  const fullLabel = rules?.required ? `${label} *` : label;

  return (
    <FormGroup>
      <Paper
        variant="outlined"
        sx={{
          padding: 2,
        }}
      >
        <FormLabel sx={{marginTop: 1}}>{fullLabel}</FormLabel>
        <Autocomplete
          sx={{marginTop: 1}}
          options={possibleValues}
          autoHighlight
          autoSelect
          fullWidth
          selectOnFocus
          handleHomeEndKeys
          freeSolo
          filterOptions={filterOptions}
          value={value}
          onChange={handleChange}
          inputValue={value}
          onInputChange={handleInputChange}
          renderInput={params => <TextField {...params} label={inputLabel} />}
        />
        <FormHelperText error={name in errors}>{errorMessage}</FormHelperText>
      </Paper>
    </FormGroup>
  );
}

ComboBox.propTypes = {
  name: string.isRequired,
  label: string.isRequired,
  inputLabel: string,
  rules: object,
  defaultValue: string,
  possibleValues: arrayOf(string).isRequired,
};

export default ComboBox;
